import classNames from 'classnames';
import _ from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilState, useRecoilValue } from 'recoil';
import { PolicyFilter } from '../components/policies/PolicyFilter';
import PolicyFilterItem from '../components/policies/PolicyFilterItem';
import RuleContainer from '../components/policies/RuleContainer';
import { AgentCategoryId } from '../resourcePolicy/agentData/agent.types';
import { getAgentIcon } from '../resourcePolicy/agentData/agentDataHelper';
import { resourcePolicyAgentsMap } from '../simulation/store/resourcePolicy.state';
import { simulationIsEditable } from '../simulation/store/simulation.state';
import {
  waypointPolicyRule,
  waypointPolicySelectedIdentity,
} from '../simulation/store/waypointPolicy/waypointPolicy.state';
import { sidebarStateByType } from '../store/sidebar.state';
import WaypointPolicyRuleSection from './WaypointPolicyRuleSection';

export type WaypointPolicyRuleProps = {
  ruleId: string;
  index?: number;
  hasIssue?: boolean;
  isDisabled?: boolean;
  color?: [string, string];
  canDelete?: boolean;
  onDeleteClick?: () => void;
  canRename?: boolean;
};

const WaypointPolicyRule: React.FC<WaypointPolicyRuleProps> = props => {
  const { t } = useTranslation('simulation');
  const [rule, updateRule] = useRecoilState(waypointPolicyRule(props.ruleId));
  const [selectedIdentity, setSelectedIdentity] = useRecoilState(
    waypointPolicySelectedIdentity,
  );
  const [policyEditPanelState, setPolicyEditPanelState] = useRecoilState(
    sidebarStateByType('sidebar-routing-policy-editor'),
  );
  const agents = useRecoilValue(resourcePolicyAgentsMap);
  const canUpdate = useRecoilValue(simulationIsEditable);

  const { ruleId } = props;

  function renameRule(newTitle: string) {
    updateRule({
      ...rule,
      title: newTitle,
    });
  }

  const isSelectedRule = ruleId === selectedIdentity?.ruleId;

  const currentAgents = _.filter(rule.agentIds, id => _.has(agents, id));
  const isActive = selectedIdentity?.ruleId === rule.id;
  const isActiveAgentFilters =
    isActive && selectedIdentity?.isRuleAgentSelected;

  const hasIssue = false;
  const hasAgentIssue = _.isEmpty(currentAgents);

  function toggleAgentSelection(shouldSelect: boolean) {
    setSelectedIdentity(
      shouldSelect
        ? {
            ruleId,
            isRuleAgentSelected: true,
          }
        : null,
    );
    setPolicyEditPanelState({
      ...policyEditPanelState,
      isCollapsed: !policyEditPanelState.isPinned
        ? !shouldSelect
        : policyEditPanelState.isCollapsed,
      isHidden: !shouldSelect,
    });
  }

  return (
    <RuleContainer
      dataComponent="WaypointPolicyRule"
      key={`policy-waypoint-rule-${ruleId}`}
      id={`policy-waypoint-rule-${ruleId}`}
      title={rule.title}
      hasIcon={true}
      hasColorMode={false}
      isCollapsible={true}
      isActive={isSelectedRule}
      //onClick={selectRule}
      isDisabled={!canUpdate}
      color={_.head(props.color)}
      isRemovable={true}
      onDeleteClick={props.onDeleteClick}
      canRename={true}
      onTitleChange={renameRule}
      classNameInner={classNames(
        'p-1 xl:p-2 bg-sidebar-header/70',
        'space-y-2',
      )}
    >
      <WaypointPolicyRuleSection
        ruleId={ruleId}
        groupType="start"
        title={t`Start (Locations)`}
      />
      <WaypointPolicyRuleSection
        ruleId={ruleId}
        groupType="end"
        title={t`End (Locations)`}
      />

      <PolicyFilter
        key={`PolicyFilter-for-agents-${rule.id}`}
        label={t`Agents`}
        isActive={isActiveAgentFilters}
        isDisabled={props.isDisabled}
        hasIssue={hasAgentIssue}
        onClick={() => toggleAgentSelection(!isActiveAgentFilters)}
      >
        {_.map(currentAgents, agentId => {
          const agent = agents[agentId];
          return (
            <PolicyFilterItem
              key={`PolicyFilterItem-for-agents-${agentId}`}
              isDisabled={props.isDisabled}
              isActive={isActiveAgentFilters}
              icon={getAgentIcon(agent.resourceTypeId as AgentCategoryId)}
              iconClassName={classNames(
                'w-8 h-8',
                // 'opacity-50',
                'ltr:mr-2 rtl:ml-2 ltr:ml-1 rtl:mr-1',
              )}
              value={agent?.title}
            />
          );
        })}

        {_.isEmpty(currentAgents) && (
          <PolicyFilterItem
            key={`PolicyFilterItem-for-empty-agents`}
            isDisabled={props.isDisabled}
            isActive={isActiveAgentFilters}
            value={t`No Agent selected`}
          />
        )}
      </PolicyFilter>
    </RuleContainer>
  );
};

export default WaypointPolicyRule;
